/**
 * File-based color themes.
 *
 * Add a theme by placing both files in this folder:
 *   <id>.theme.json  — metadata (label, description, swatches, optional default)
 *   <id>.theme.css   — [data-theme="<id>"] color tokens only
 *
 * Available themes = intersection of matching .json + .css basenames.
 * Missing / removed themes fall back to the site default.
 */

import './base.css';

// Webpack (CRA) discovers every theme CSS/JSON at build time.
const cssContext = require.context('.', false, /^\.\/.+\.theme\.css$/);
const metaContext = require.context('.', false, /^\.\/.+\.theme\.json$/);

cssContext.keys().forEach((key) => {
  cssContext(key);
});

function basenameFromKey(key, suffix) {
  // key like "./thezoo.theme.json"
  const name = key.replace(/^\.\//, '');
  if (!name.endsWith(suffix)) return null;
  return name.slice(0, -suffix.length).toLowerCase();
}

const cssIds = new Set(
  cssContext.keys().map((k) => basenameFromKey(k, '.theme.css')).filter(Boolean),
);

function loadThemeMeta(fileId) {
  const key = `./${fileId}.theme.json`;
  let raw = {};
  try {
    raw = metaContext(key);
  } catch {
    return null;
  }
  // require.context JSON may be the object itself or { default: ... }
  const meta = raw && typeof raw === 'object' && raw.default && typeof raw.default === 'object'
    ? raw.default
    : raw;
  if (!meta || typeof meta !== 'object') return null;

  const id = String(meta.id || fileId).trim().toLowerCase() || fileId;
  const swatches = meta.swatches && typeof meta.swatches === 'object' ? meta.swatches : {};
  return {
    id,
    label: String(meta.label || id),
    description: String(meta.description || ''),
    default: !!meta.default,
    swatches: {
      bg: swatches.bg || '#111',
      fg: swatches.fg || '#eee',
      accent: swatches.accent || '#888',
    },
  };
}

/** Themes with both meta JSON and CSS present. */
export const THEMES = metaContext.keys()
  .map((k) => basenameFromKey(k, '.theme.json'))
  .filter((id) => id && cssIds.has(id))
  .map((fileId) => loadThemeMeta(fileId))
  .filter(Boolean)
  // Prefer default first, then label A–Z
  .sort((a, b) => {
    if (a.default && !b.default) return -1;
    if (!a.default && b.default) return 1;
    return a.label.localeCompare(b.label);
  });

const VALID = new Set(THEMES.map((t) => t.id));

const markedDefault = THEMES.find((t) => t.default);
export const DEFAULT_THEME = markedDefault?.id
  || (VALID.has('fadeddreamz') ? 'fadeddreamz' : null)
  || THEMES[0]?.id
  || 'fadeddreamz';

export const THEME_STORAGE_KEY = 'cards:theme';

export function isThemeAvailable(themeId) {
  return VALID.has(String(themeId || '').trim().toLowerCase());
}

/** Unknown / removed theme ids → site default. */
export function normalizeThemeId(value) {
  const id = String(value || '').trim().toLowerCase();
  return VALID.has(id) ? id : DEFAULT_THEME;
}

/** Apply theme to <html> and persist locally for fast paint next visit. */
export function applyTheme(themeId) {
  const id = normalizeThemeId(themeId);
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', id);
    try {
      window.dispatchEvent(new CustomEvent('cards:theme', { detail: { id } }));
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    /* private mode */
  }
  return id;
}

export function getStoredTheme() {
  try {
    return normalizeThemeId(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

/** Run before React paints when possible (also safe from index.js). */
export function bootstrapTheme() {
  return applyTheme(getStoredTheme());
}
